/**
 * Unified Worker/Employee Types
 *
 * Centralized worker/employee interface used across all modules:
 * - LexShift (HR Management)
 * - WaterAndIce (Production)
 * - Future modules
 *
 * Database table: shop_floor_workers
 */

// ── Kontrakt PIN-u kiosku ────────────────────────────────────────────────────
//
// Jedyne źródło prawdy dla długości PIN-u po stronie klientów. Reguły są DWIE
// i odpowiadają na dwa różne pytania — nie są kopią jednej:
//
//   * ZAPIS (`WORKER_PIN_*`) — jaki PIN wolno dziś NADAĆ pracownikowi:
//     **dokładnie 4 cyfry**. Decyzja właściciela z 06.10.2026 („dokładnie
//     4 cyfry wszędzie”): tyle przyjmuje terminal Receptora (`PinFormat`,
//     `PUT /api/User/{id}/Pin` odpowiada 400 na każdą inną długość) i tyle
//     przyjmują natywne klawiatury Androida (`PIN_LENGTH = 4`). Plan 52
//     (`docs/09-STRATEGIA-BIZNES/52-PLAN-P2-PIN-Z-UNIVERSE-DO-RECEPTORA.md`),
//     etap P2-1.
//   * LOGOWANIE (`WORKER_PIN_LOGIN_*`) — jaki PIN klawiatura musi dać się
//     WPISAĆ: 4–8 cyfr, tyle co `shop_floor_verify_pin` (269). PIN-y 5–8 cyfr
//     nadane przed P2-1 leżą w bazie jako bcrypt i nie da się ich wykryć ani
//     przenieść (§5 planu 52) — klawiatura ucięta na czwartej cyfrze odcięłaby
//     tych ludzi od logowania bez żadnego komunikatu.
//
// Baza do etapu P2-3 jest SZERSZA niż reguła zapisu: wyzwalacz
// `shop_floor_workers_pin_bi` (269) nadal przyjmuje `^[0-9]{4,8}$`. Zawęża go
// dopiero migracja M4 planu 52 — i to jest powód, dla którego ten etap idzie
// osobnym merge'em PRZED nią: M4 odrzuciłaby 5–8 cyfr, które formularz
// wdrożony przed P2-1 jeszcze wysyła (`CLAUDE.md`, „dwa merge'e”). Bramka
// `tests/unit/supabase/worker-pin-contract.test.ts` pilnuje, że reguła zapisu
// mieści się w regule wyzwalacza, a reguła logowania jest regułą
// `shop_floor_verify_pin`.

/** Długość PIN-u nadawanego pracownikowi — dokładnie tyle cyfr, ani mniej, ani więcej. */
export const WORKER_PIN_LENGTH = 4;

/** Najkrótszy PIN, jaki wolno zapisać. */
export const WORKER_PIN_MIN_LENGTH = WORKER_PIN_LENGTH;

/** Najdłuższy PIN, jaki wolno zapisać (pole formularza ucina dalsze cyfry). */
export const WORKER_PIN_MAX_LENGTH = WORKER_PIN_LENGTH;

/** Pełna reguła formatu zapisu — same cyfry, dokładnie 4 znaki. */
export const WORKER_PIN_PATTERN = new RegExp(`^[0-9]{${WORKER_PIN_LENGTH}}$`);

/** Krótka podpowiedź do pola PIN-u (placeholder). */
export const WORKER_PIN_FORMAT_LABEL = `dokładnie ${WORKER_PIN_LENGTH} cyfry`;

/** Komunikat pokazywany użytkownikowi przy PIN-ie w złym formacie. */
export const WORKER_PIN_HINT = `PIN musi składać się z dokładnie ${WORKER_PIN_LENGTH} cyfr.`;

/** Czy `pin` wolno zapisać pracownikowi. Puste/`undefined` traktujemy jak brak zmiany. */
export function isValidWorkerPin(pin: string | null | undefined): boolean {
  return !!pin && WORKER_PIN_PATTERN.test(pin);
}

/**
 * Najkrótszy PIN, jaki klawiatura logowania pozwala wysłać — reguła
 * `shop_floor_verify_pin` (269). Wspólny z regułą zapisu z definicji: PIN
 * krótszy niż 4 cyfry nie istniał w bazie nigdy.
 */
export const WORKER_PIN_LOGIN_MIN_LENGTH = WORKER_PIN_MIN_LENGTH;

/**
 * Najdłuższy PIN, jaki klawiatura logowania pozwala wpisać — reguła
 * `shop_floor_verify_pin` (269), `^[0-9]{4,8}$`. NIE zawężać do 4 razem
 * z regułą zapisu: pracownik z PIN-em nadanym przed 06.10.2026 straciłby
 * logowanie (plan 52, §5). Używają tego wyłącznie ekrany LOGOWANIA.
 */
export const WORKER_PIN_LOGIN_MAX_LENGTH = 8;

/**
 * Długość PIN-u nadawanego automatycznie. Cztery, nie więcej: natywne ekrany
 * Androida (`PinLoginViewModel`, `KioskViewModel`, `WorkerLoginViewModel`
 * w `apps/temperatio-android`) mają `PIN_LENGTH = 4` i wysyłają PIN po czwartej
 * cyfrze, więc dłuższy PIN wygenerowany tutaj byłby PIN-em, którym nie da się
 * zalogować na telefonie.
 */
export const WORKER_PIN_GENERATED_LENGTH = WORKER_PIN_LENGTH;

/**
 * Czy PIN jest trywialny do zgadnięcia: wszystkie cyfry takie same (`1111`)
 * albo kolejne rosnąco/malejąco (`1234`, `9876`). Takich PIN-ów generator
 * nie wydaje — człowiek może je wpisać ręcznie, generator nie powinien.
 */
export function isTrivialWorkerPin(pin: string): boolean {
  if (pin.length < 2) return true;
  const d = Array.from(pin, Number);
  const step = d[1] - d[0];
  if (step !== 0 && Math.abs(step) !== 1) return false;
  return d.every((v, i) => i === 0 || v - d[i - 1] === step);
}

/**
 * Losuje PIN pracownika z kryptograficznego źródła losowości
 * (`crypto.getRandomValues`, nie `Math.random`). Unikalności w organizacji NIE
 * sprawdza — nie może, bo PIN-y leżą w bazie wyłącznie jako solone hashe.
 * Rozstrzyga ją wyzwalacz `shop_floor_workers_pin_bi` (269) błędem `pin_taken`;
 * ekran po takim błędzie losuje kolejny.
 */
export function generateWorkerPin(length: number = WORKER_PIN_GENERATED_LENGTH): string {
  if (
    !Number.isInteger(length) ||
    length < WORKER_PIN_MIN_LENGTH ||
    length > WORKER_PIN_MAX_LENGTH
  ) {
    throw new RangeError(WORKER_PIN_HINT);
  }
  const buf = new Uint32Array(length);
  for (;;) {
    globalThis.crypto.getRandomValues(buf);
    // 2^32 nie dzieli się przez 10 — odrzucamy górny ogon, żeby cyfry były równomierne.
    const limit = 4294967290; // największa wielokrotność 10 nie większa niż 2^32
    if (buf.some((v) => v >= limit)) continue;
    const pin = Array.from(buf, (v) => String(v % 10)).join('');
    if (!isTrivialWorkerPin(pin)) return pin;
  }
}

/**
 * Core worker interface - represents an employee in the system
 * Combines fields from LexShift (Employee) and WaterAndIce (ShopFloorWorker)
 */
export interface ShopFloorWorker {
  id: string;
  organization_id: string;

  // Location association (optional - for site-specific workers)
  location_id?: string;

  // User account linkage (for workers with system access)
  user_id?: string;

  // Personal information
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;

  // Organizational structure (LexShift HR)
  section_id?: string;
  position_id?: string;
  position?: string; // Legacy/fallback for position name

  /**
   * Kanał ZAPISU PIN-u kiosku — nigdy nie przechowuje sekretu. Trigger
   * `shop_floor_workers_pin_bi` (migracja 269) zamienia go na `pin_hash`
   * (bcrypt) i zeruje kolumnę. Długość musi spełniać {@link WORKER_PIN_PATTERN}.
   */
  pin?: string;

  // Status
  is_active: boolean;

  /**
   * Czy pracownik ma zapisany wzorzec twarzy (migracja 203). Flaga jest
   * POCHODNA — podnosi ją zapis wiersza w `lexshift_face_embeddings`, więc
   * nie ustawia się jej wprost. Sam wzorzec to wektor cech; zdjęcie nie jest
   * nigdzie przechowywane, a z wektora twarzy nie da się odtworzyć.
   */
  face_registered?: boolean;

  // Activity tracking
  last_login_at?: string;
  last_device_id?: string;

  // Module-specific preferences and settings
  preferences?: WorkerPreferences;

  // Timestamps
  created_at: string;
  updated_at: string;
}

/**
 * Worker preferences stored as JSONB
 */
export interface WorkerPreferences {
  language?: string;
  temperature_unit?: 'celsius' | 'fahrenheit';
  notifications_enabled?: boolean;
  default_location_id?: string;
  [key: string]: unknown;
}

/**
 * Input for creating a new worker
 */
export type CreateWorkerInput = Omit<ShopFloorWorker, 'id' | 'created_at' | 'updated_at'>;

/**
 * Input for updating a worker
 */
export type UpdateWorkerInput = Partial<
  Omit<ShopFloorWorker, 'id' | 'organization_id' | 'created_at' | 'updated_at'>
>;

/**
 * Alias for backward compatibility with LexShift
 */
export type Employee = ShopFloorWorker;

/**
 * Alias for backward compatibility with WaterAndIce
 */
export type WaterAndIceWorker = ShopFloorWorker;

/**
 * Worker with joined relations (for queries)
 */
export interface WorkerWithRelations extends ShopFloorWorker {
  section?: {
    id: string;
    name: string;
  };
  position_rel?: {
    id: string;
    name: string;
  };
  location?: {
    id: string;
    name: string;
  };
  /** Organizational units this worker is assigned to (new hierarchy) */
  organizational_units?: {
    id: string;
    name: string;
    unit_type: string;
    is_primary: boolean;
    role_in_unit?: string;
  }[];
  /** Equipment assigned directly to this worker */
  assigned_equipment?: {
    id: string;
    name: string;
    type: string;
  }[];
}

/**
 * Worker summary for lists and dropdowns
 */
export interface WorkerSummary {
  id: string;
  first_name: string;
  last_name: string;
  position?: string;
  is_active: boolean;
}

/**
 * Helper to get worker full name
 */
export function getWorkerFullName(
  worker: Pick<ShopFloorWorker, 'first_name' | 'last_name'>
): string {
  return `${worker.first_name} ${worker.last_name}`;
}
